import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import Header from "../../components/header";
import RegionalPlate from "../../components/regionalplate";
import RegionalStoreMap from "../../components/regionalstoremap";
import SeoHead from "../../components/seoHead";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { Stores } from "../../dataLists/index";
import { dataListsType } from "../../shared/type";
import Footer from "../../components/footer";
import { useLocalePath, useAppLocale } from "../../i18n/LocaleRouting";
import { regionLabel } from "../../i18n/display";
import { absoluteLocalizedUrl } from "../../i18n/seoUrls";
import {
  buildStoreDistanceMap,
  detectPrefectureFromCoords,
  findNearbyStoresWithAutoExpand,
  LatLng,
  NEARBY_RADIUS_OPTIONS,
  NearbyRadiusKm,
  prefectureToSlug,
  prefecturesMatch,
  slugToPrefecture,
} from "../../utils/geo";
import {
  readStoredMapTheme,
  writeStoredMapTheme,
  type MapTheme,
} from "../../utils/mapStyles";

import { FloatButton, Modal, Select, message } from "antd";
import { useTranslation } from "react-i18next";

type LocationStatus = "idle" | "loading" | "denied" | "error" | "ready";

type RegionalStoreLocationState = {
  userPosition?: LatLng;
  activateNearby?: boolean;
};

const RegionalStore: React.FC = () => {
  const { t } = useTranslation();
  const locale = useAppLocale();
  const { area } = useParams();
  const navigate = useNavigate();
  const localePath = useLocalePath();
  const routerLocation = useLocation();
  const normalizedArea = area?.replace(/-/g, " ");
  const displayArea = regionLabel(t, normalizedArea ?? area ?? "");
  const areaPath = `/regionalstore/${area}`;
  const areaUrl = absoluteLocalizedUrl(areaPath, locale);
  const homeUrl = absoluteLocalizedUrl("/", locale);
  const allRegionsUrl = absoluteLocalizedUrl("/regionalstore", locale);
  const [selectedStoreId, setSelectedStoreId] = useState<number | undefined>(
    undefined,
  );
  const mapSectionRef = useRef<HTMLDivElement | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<string>("All");
  const [mapTheme, setMapTheme] = useState<MapTheme>(() =>
    readStoredMapTheme(),
  );

  const handleMapThemeToggle = () => {
    setMapTheme((prev) => {
      const next: MapTheme = prev === "night" ? "standard" : "night";
      writeStoredMapTheme(next);
      return next;
    });
  };

  // --- GPS / nearby stores ---
  const [locationStatus, setLocationStatus] = useState<LocationStatus>("idle");
  const [userPosition, setUserPosition] = useState<LatLng | null>(null);
  const [nearbyActive, setNearbyActive] = useState(false);
  const [preferredRadiusKm, setPreferredRadiusKm] = useState<NearbyRadiusKm>(3); // 用戶選擇的搜尋半徑
  const [effectiveRadiusKm, setEffectiveRadiusKm] = useState<number>(3); // 自動擴展後實際使用的半徑
  const [nearbyNotice, setNearbyNotice] = useState<string | null>(null);

  // GPS 判斷用戶在其他 prefecture 時，暫存座標並等待用戶確認是否跳轉
  const [regionSwitchModal, setRegionSwitchModal] = useState<{
    open: boolean;
    targetPrefecture: string;
    pendingPosition: LatLng | null;
  }>({ open: false, targetPrefecture: "", pendingPosition: null });

  const areaData = useMemo<dataListsType[]>(() => {
    const filteredAreaData = Stores.filter(
      (item) =>
        item.prefecture?.toLowerCase() === normalizedArea?.toLowerCase(),
    );
    return filteredAreaData as unknown as dataListsType[];
  }, [normalizedArea]);

  const districtOptions = useMemo(() => {
    const districts = new Set<string>();

    areaData.forEach((store) => {
      if (store.district && store.district !== "Tokyo") {
        districts.add(store.district);
      }
    });

    return ["All", ...Array.from(districts).sort()];
  }, [areaData]);

  const filteredStores = useMemo(() => {
    if (selectedDistrict === "All") {
      return areaData;
    }

    return areaData.filter((store) => store.district === selectedDistrict);
  }, [areaData, selectedDistrict]);

  /** 依用戶座標啟用附近店家模式；半徑內無店時會自動 3→5→10 km 擴展 */
  const activateNearbyForPosition = useCallback(
    (position: LatLng, radiusKm: NearbyRadiusKm = preferredRadiusKm) => {
      const result = findNearbyStoresWithAutoExpand(
        filteredStores,
        position,
        radiusKm,
      );

      setUserPosition(position);
      setLocationStatus("ready");
      setEffectiveRadiusKm(result.effectiveRadiusKm);

      // 10 km 內仍無店：放棄附近模式，回到顯示全區
      if (result.exhausted) {
        setNearbyActive(false);
        setNearbyNotice(
          `No stores within 10 km. Showing all stores in ${normalizedArea ?? area}.`,
        );
        message.info(
          `No stores within 10 km. Showing all stores in ${normalizedArea ?? area}.`,
        );
        return;
      }

      setNearbyActive(true);
      // 自動擴展半徑時提示用戶（例如選 3 km 但實際顯示 5 km）
      setNearbyNotice(
        result.effectiveRadiusKm > radiusKm
          ? `No stores within ${radiusKm} km. Showing stores within ${result.effectiveRadiusKm} km.`
          : null,
      );
    },
    [area, filteredStores, normalizedArea, preferredRadiusKm],
  );

  /** 點擊「Use my location」：取 GPS → 判斷 prefecture → 啟用附近模式或彈出跨區提示 */
  const handleUseMyLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationStatus("error");
      message.error("Geolocation is not supported on this device.");
      return;
    }

    setLocationStatus("loading");
    setNearbyNotice(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords: LatLng = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        const detectedPrefecture = detectPrefectureFromCoords(coords);
        const currentPrefecture = slugToPrefecture(area ?? "");

        // 用戶實際位置與 URL 區域不符（如在 Sydney 頁但人在 Tokyo）
        if (
          detectedPrefecture &&
          !prefecturesMatch(detectedPrefecture, currentPrefecture)
        ) {
          setLocationStatus("idle");
          setRegionSwitchModal({
            open: true,
            targetPrefecture: detectedPrefecture,
            pendingPosition: coords,
          });
          return;
        }

        activateNearbyForPosition(coords);
      },
      () => {
        setLocationStatus("denied");
        setNearbyActive(false);
        setUserPosition(null);
        message.info(
          "Location access denied. Showing all stores in this region.",
        );
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 },
    );
  }, [activateNearbyForPosition, area]);

  /** 退出附近模式，恢復顯示當前 prefecture 全部店家 */
  const handleClearNearby = useCallback(() => {
    setNearbyActive(false);
    setUserPosition(null);
    setLocationStatus("idle");
    setNearbyNotice(null);
    setEffectiveRadiusKm(preferredRadiusKm);
  }, [preferredRadiusKm]);

  /** 跨區確認後跳轉，並透過 router state 帶入 GPS 座標以自動啟用附近模式 */
  const handleConfirmRegionSwitch = useCallback(() => {
    const { targetPrefecture, pendingPosition } = regionSwitchModal;
    setRegionSwitchModal({
      open: false,
      targetPrefecture: "",
      pendingPosition: null,
    });

    if (!pendingPosition) return;

    navigate(localePath(`/regionalstore/${prefectureToSlug(targetPrefecture)}`), {
      state: {
        userPosition: pendingPosition,
        activateNearby: true,
      } satisfies RegionalStoreLocationState,
    });
  }, [localePath, navigate, regionSwitchModal]);

  // 跨區跳轉後從 router state 讀取 GPS，啟用附近模式並清除 state 避免重複觸發
  useEffect(() => {
    const state = routerLocation.state as RegionalStoreLocationState | null;
    if (!state?.activateNearby || !state.userPosition) return;

    activateNearbyForPosition(state.userPosition);
    navigate(routerLocation.pathname, { replace: true, state: null });
  }, [
    activateNearbyForPosition,
    navigate,
    routerLocation.pathname,
    routerLocation.state,
  ]);

  // 用戶切換 3/5/10 km 時重新篩選
  useEffect(() => {
    if (!nearbyActive || !userPosition) return;
    activateNearbyForPosition(userPosition, preferredRadiusKm);
  }, [preferredRadiusKm]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSelectedStoreId(undefined);
  }, [selectedDistrict]);

  // 切換 district 篩選時，在現有 GPS 下重新計算附近店家
  useEffect(() => {
    if (!nearbyActive || !userPosition) return;
    activateNearbyForPosition(userPosition, preferredRadiusKm);
  }, [filteredStores]); // eslint-disable-line react-hooks/exhaustive-deps

  /** 附近模式下的店家子集（含自動擴展半徑邏輯） */
  const nearbyStores = useMemo(() => {
    if (!nearbyActive || !userPosition) return filteredStores;

    const result = findNearbyStoresWithAutoExpand(
      filteredStores,
      userPosition,
      preferredRadiusKm,
    );
    return result.stores;
  }, [filteredStores, nearbyActive, preferredRadiusKm, userPosition]);

  /** 供列表與 InfoWindow 顯示距離（km） */
  const storeDistances = useMemo(() => {
    if (!userPosition) return undefined;
    const distanceMap = buildStoreDistanceMap(nearbyStores, userPosition);
    return Object.fromEntries(distanceMap);
  }, [nearbyStores, userPosition]);

  // 附近模式：地圖與列表只顯示半徑內店家；否則顯示 district 篩選後的全部店家
  const mapStores = nearbyActive ? nearbyStores : filteredStores;

  const mapControlSelectClassName = useMemo(
    () =>
      mapTheme === "night"
        ? [
            "h-[25px] text-sm",
            "[&_.ant-select-selector]:!h-[25px] [&_.ant-select-selector]:!min-h-[25px]",
            "[&_.ant-select-selector]:!px-2 [&_.ant-select-selector]:!rounded-[4px]",
            "[&_.ant-select-selector]:!bg-black/20 [&_.ant-select-selector]:!border-gray-500",
            "[&_.ant-select-selector]:!shadow-md [&_.ant-select-selector]:!items-center",
            "[&_.ant-select-selection-item]:!text-white/80 [&_.ant-select-selection-item]:!leading-[23px]",
            "[&_.ant-select-selection-item]:!font-medium",
            "[&_.ant-select-arrow]:!text-white/80 [&_.ant-select-arrow]:!inset-inline-end-2",
          ].join(" ")
        : [
            "h-[25px] text-sm",
            "[&_.ant-select-selector]:!h-[25px] [&_.ant-select-selector]:!min-h-[25px]",
            "[&_.ant-select-selector]:!px-2 [&_.ant-select-selector]:!rounded-[4px]",
            "[&_.ant-select-selector]:!bg-white/60 [&_.ant-select-selector]:!border-gray-200",
            "[&_.ant-select-selector]:!shadow-md [&_.ant-select-selector]:!items-center",
            "[&_.ant-select-selection-item]:!text-[#1d1d1d] [&_.ant-select-selection-item]:!leading-[23px]",
            "[&_.ant-select-selection-item]:!font-medium",
            "[&_.ant-select-arrow]:!text-[#1d1d1d] [&_.ant-select-arrow]:!inset-inline-end-2",
          ].join(" "),
    [mapTheme],
  );

  const mapControlSelectPopupClassName = useMemo(
    () =>
      mapTheme === "night"
        ? [
            "map-control-select-dropdown",
            "!p-1 !rounded-[4px] !bg-[#1a1a1a]/95 !border !border-gray-500 !shadow-md",
            "[&_.ant-select-item]:!text-white/80 [&_.ant-select-item]:!rounded-[4px]",
            "[&_.ant-select-item-option-active]:!bg-white/10",
            "[&_.ant-select-item-option-selected]:!bg-white/20 [&_.ant-select-item-option-selected]:!text-white",
            "[&_.ant-select-item-option-selected]:!font-medium",
          ].join(" ")
        : [
            "map-control-select-dropdown",
            "!p-1 !rounded-[4px] !bg-white/95 !border !border-gray-200 !shadow-md",
            "[&_.ant-select-item]:!text-[#1d1d1d] [&_.ant-select-item]:!rounded-[4px]",
            "[&_.ant-select-item-option-active]:!bg-black/5",
            "[&_.ant-select-item-option-selected]:!bg-black/10 [&_.ant-select-item-option-selected]:!text-[#1d1d1d]",
            "[&_.ant-select-item-option-selected]:!font-medium",
          ].join(" "),
    [mapTheme],
  );

  const handleStoreSelect = useCallback((storeId: number) => {
    setSelectedStoreId((prevSelectedStoreId) =>
      prevSelectedStoreId === storeId ? undefined : storeId,
    );
    mapSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  return (
    <>
      <SeoHead
        title={t("seo.regionalArea.title", { area: displayArea })}
        description={t("seo.regionalArea.description", { area: displayArea })}
        path={areaPath}
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: t("seo.regionalArea.title", { area: displayArea }),
            url: areaUrl,
            description: t("seo.regionalArea.description", {
              area: displayArea,
            }),
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: t("common.home"),
                item: homeUrl,
              },
              {
                "@type": "ListItem",
                position: 2,
                name: t("allRegions.title"),
                item: allRegionsUrl,
              },
              {
                "@type": "ListItem",
                position: 3,
                name: displayArea,
                item: areaUrl,
              },
            ],
          },
        ]}
      />
      <Header />
      <main className="relative w-full h-full flex justify-center items-center">
        <article className="w-full max-w-[1400px] md:p-10 p-8 flex flex-col justify-center items-center gap-y-4">
          <div ref={mapSectionRef} className="w-full relative">
            <RegionalStoreMap
              stores={mapStores}
              selectedStoreId={selectedStoreId}
              onMarkerClick={handleStoreSelect}
              userPosition={nearbyActive ? userPosition : null}
              nearbyRadiusKm={nearbyActive ? effectiveRadiusKm : undefined}
              storeDistances={storeDistances}
              mapTheme={mapTheme}
            />

            {/* <div className="absolute top-3 right-3 flex flex-col items-end gap-2 z-10 px-2 py-1 rounded-md bg-black/20 backdrop-blur-md border border-orange-300 shadow-md hover:border-[#F05A2A]"> */}
            <div
              className={`
                  absolute top-3 right-3 flex flex-col items-end gap-2 z-10 px-3 py-2 rounded-md  backdrop-blur-md border-0 border-gray-300 shadow-md
                 ${mapTheme === "night" ? "bg-gray-400/30" : "bg-white/40"}
                 `}
            >
              {/* <div className="absolute top-[3%] right-3 z-10 flex flex-col items-end gap-2"> */}
              <div>
                {!nearbyActive ? (
                  <button
                    type="button"
                    onClick={handleUseMyLocation}
                    disabled={locationStatus === "loading"}
                    className={`
                      px-1 h-[25px] rounded-[4px]  border shadow-md text-sm font-medium disabled:opacity-60 
                       ${mapTheme === "night" ? "bg-black/20 text-white/80 border-gray-500" : "bg-white/60 text-[#1d1d1d] border-gray-200"}
                      `}
                  >
                    {locationStatus === "loading"
                      ? "Locating..."
                      : "Use my location"}
                  </button>
                ) : (
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <Select
                      size="small"
                      value={preferredRadiusKm}
                      onChange={(value) =>
                        setPreferredRadiusKm(value as NearbyRadiusKm)
                      }
                      options={NEARBY_RADIUS_OPTIONS.map((radius) => ({
                        value: radius,
                        label: `${radius} km`,
                      }))}
                      className={`min-w-[88px] ${mapControlSelectClassName}`}
                      popupClassName={mapControlSelectPopupClassName}
                    />
                    <button
                      type="button"
                      onClick={handleClearNearby}
                      // className=""
                      className={`
                       px-3 h-[25px] rounded-[4px] border shadow-md text-sm font-medium
                         ${mapTheme === "night" ? "bg-black/20 text-white/80 border-gray-500" : "bg-white/60 text-[#1d1d1d] border-gray-200"}
                        `}
                    >
                      Show all
                    </button>
                  </div>
                )}
              </div>

              {/* <div className="absolute top-[9%] right-3 z-10"> */}
              <div>
                <button
                  type="button"
                  onClick={handleMapThemeToggle}
                  className={`
                    px-2 py-1 flex items-center justify-center rounded-md border shadow-md text-xs font-medium  
                    ${mapTheme === "night" ? "bg-black/20 border-gray-500" : "bg-white/60 border-gray-200"}
                    `}
                  aria-label={
                    mapTheme === "night"
                      ? "Switch to standard map"
                      : "Switch to night map"
                  }
                >
                  {/* {mapTheme === "night" ? "Night" : "Standard"} */}
                  <span
                    className={`${mapTheme === "night" ? "text-[#F05A2A]" : "text-[#1d1d1d]"}`}
                  >
                    Night
                  </span>
                  <span
                    className={`${mapTheme === "night" ? "text-white/80" : "text-[#1d1d1d]"} px-[2px]`}
                  >
                    /
                  </span>
                  <span
                    className={`${mapTheme === "night" ? "text-white/80" : "text-[#F05A2A]"}`}
                  >
                    Standard
                  </span>
                </button>
              </div>
              {/* <div className="absolute top-[15%] right-3 z-10"> */}
              <div>
                <Select
                  size="small"
                  value={selectedDistrict}
                  onChange={setSelectedDistrict}
                  options={districtOptions.map((district) => ({
                    value: district,
                    label: district === "All" ? "All location" : district,
                  }))}
                  className={`min-w-[120px] max-w-[180px] ${mapControlSelectClassName}`}
                  popupClassName={mapControlSelectPopupClassName}
                  popupMatchSelectWidth={false}
                  aria-label="Filter by district"
                />
              </div>
            </div>

            {nearbyNotice && (
              <div className="mt-2 text-sm text-[#1d1d1d]/70">
                {nearbyNotice}
              </div>
            )}
          </div>

          <RegionalPlate
            props={mapStores}
            allStores={areaData}
            selectedDistrict={selectedDistrict}
            selectedStoreId={selectedStoreId}
            onStoreClick={(store) => handleStoreSelect(store.id)}
            storeDistances={storeDistances}
            sortByDistance={nearbyActive}
          />
        </article>
      </main>
      <Footer />

      <FloatButton.BackTop />

      {/* GPS 與 URL 區域不符時詢問是否跳轉（如 Sydney 頁 → Tokyo） */}
      <Modal
        title="Switch region?"
        open={regionSwitchModal.open}
        onOk={handleConfirmRegionSwitch}
        onCancel={() =>
          setRegionSwitchModal({
            open: false,
            targetPrefecture: "",
            pendingPosition: null,
          })
        }
        okText={`Go to ${regionSwitchModal.targetPrefecture}`}
        cancelText="Stay here"
      >
        <p>
          You appear to be in{" "}
          <strong>{regionSwitchModal.targetPrefecture}</strong>. Switch to{" "}
          {regionSwitchModal.targetPrefecture} stores and show nearby shops?
        </p>
      </Modal>
    </>
  );
};

export default RegionalStore;
